import styled from '@emotion/styled';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useAttachments } from '../hooks/useAttachments';
import type { AttachmentDraft, ChatAttachment } from '../models/attachment';
import {
  ATTACHMENT_ACCEPT_ATTRIBUTE,
  attachmentTypeLabel,
  hasUploadingAttachment,
  isImageMimeType,
  MAX_ATTACHMENTS,
  toChatAttachments,
} from '../services/attachment';
import {
  colorAgentBorder,
  colorAgentInputBorder,
  colorAgentInputFocusBorder,
  colorAgentTextDisabled,
  colorAgentTextPrimary,
  colorAgentTextSecondary,
  colorNeutralGray,
  colorNeutralWhite,
  colorPrimaryDeepBlue,
  colorSemanticError,
  fontFamilyJa,
  fontSizeSm,
  fontSizeXs,
  lineHeightNormal,
  radiusSm,
  space1,
  space2,
  space3,
} from '../styles/tokens';

const MAX_LENGTH = 2000;

const Wrapper = styled(Box)`
  border-top: 1px solid ${colorAgentBorder};
`;

const ComposerArea = styled(Box)`
  padding: 10px ${space3} ${space1};
`;

/** border と focus 表現を持つのはここだけで、textarea は自分で枠を描かない。 */
const ComposerShell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colorAgentInputBorder};
  border-radius: ${radiusSm};
  /* disabled は composer 全体を灰色にする（textarea だけの小箱にしない） */
  background-color: ${({ isDisabled }) => (isDisabled ? colorNeutralGray : colorNeutralWhite)};
  overflow: hidden;

  &:focus-within {
    border-color: ${colorAgentInputFocusBorder};
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: none;
  border: 0;
  outline: none;
  background-color: transparent;
  padding: 10px ${space3} ${space1};
  font-size: ${fontSizeSm};
  font-family: ${fontFamilyJa};
  line-height: ${lineHeightNormal};
  min-height: 40px;
  max-height: 100px;
  overflow-y: auto;
  color: ${colorAgentTextPrimary};

  &:disabled {
    color: ${colorAgentTextDisabled};
  }
`;

const ComposerFooter = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  padding: 2px 6px 6px ${space2};
`;

const ComposerFooterLeft = styled(Box)`
  display: flex;
  align-items: center;
`;

const ComposerFooterRight = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${space2};
`;

const SendButton = styled(IconButton)`
  background-color: ${colorPrimaryDeepBlue};
  color: ${colorNeutralWhite};
  width: 30px;
  height: 30px;
  border-radius: 50%;

  &:hover {
    background-color: ${colorPrimaryDeepBlue};
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${colorAgentBorder};
    color: ${colorAgentTextDisabled};
  }
`;

const CharCount = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOver',
})<{ isOver: boolean }>`
  font-size: ${fontSizeXs};
  color: ${({ isOver }) => (isOver ? colorSemanticError : colorAgentTextDisabled)};
  white-space: nowrap;
`;

const Disclaimer = styled(Typography)`
  font-size: 10px;
  color: ${colorAgentTextDisabled};
  text-align: center;
  padding: 2px ${space3} 6px;
`;

const AttachmentStrip = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${space2};
  padding: ${space2} ${space3} 0;
`;

const Thumbnail = styled(Box)`
  position: relative;
  width: 48px;
  height: 48px;
  border: 1px solid ${colorAgentBorder};
  border-radius: ${radiusSm};
  overflow: hidden;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UploadingOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.6);
`;

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0;
  width: 16px;
  height: 16px;
  background-color: ${colorNeutralWhite};

  &:hover {
    background-color: ${colorNeutralGray};
  }
`;

const AttachmentError = styled(Typography)`
  font-size: ${fontSizeXs};
  color: ${colorSemanticError};
  padding: ${space1} ${space3} 0;
`;

const FileChip = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${space1};
  max-width: 180px;
  height: 48px;
  padding: 0 18px 0 6px;
  border: 1px solid ${colorAgentBorder};
  border-radius: ${radiusSm};
  background-color: ${colorNeutralWhite};
`;

const FileChipName = styled(Typography)`
  font-size: ${fontSizeXs};
  color: ${colorAgentTextPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileChipIcon = styled.span`
  font-size: 18px;
  color: ${colorAgentTextSecondary};
  display: flex;
`;

function DraftAttachment({ draft, onRemove }: { draft: AttachmentDraft; onRemove: (id: string) => void }) {
  const removeButton = (
    <RemoveButton onClick={() => onRemove(draft.id)} size="small" aria-label={`${draft.fileName} を削除`}>
      <CloseIcon sx={{ fontSize: 12 }} />
    </RemoveButton>
  );
  const uploadingOverlay = draft.status === 'uploading' && (
    <UploadingOverlay>
      <CircularProgress size={16} />
    </UploadingOverlay>
  );

  if (isImageMimeType(draft.mimeType)) {
    return (
      <Thumbnail>
        <ThumbnailImage src={draft.previewUrl} alt={draft.fileName} />
        {uploadingOverlay}
        {removeButton}
      </Thumbnail>
    );
  }

  return (
    <Tooltip title={`${draft.fileName}（${attachmentTypeLabel(draft.mimeType)}）`} placement="top">
      <FileChip>
        <FileChipIcon as={PictureAsPdfOutlinedIcon} />
        <FileChipName>{draft.fileName}</FileChipName>
        {uploadingOverlay}
        {removeButton}
      </FileChip>
    </Tooltip>
  );
}

interface ChatInputProps {
  onSend: (message: string, attachments?: ChatAttachment[]) => void;
  disabled?: boolean;
}

export function ChatInput({ onSend, disabled = false }: ChatInputProps) {
  const [value, setValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { drafts, error: attachmentError, addFiles, removeDraft, clearDrafts } = useAttachments();
  const isOver = value.length > MAX_LENGTH;
  const isUploading = hasUploadingAttachment(drafts);
  // アップロード中は送信させない（S3 key が揃うまで attachments を確定できない）
  const canSend = value.trim().length > 0 && !isOver && !disabled && !isUploading;

  // CSS だけでは textarea の高さが内容に追従しないため、入力に合わせて伸ばす（上限は max-height）。
  useLayoutEffect(() => {
    const element = textAreaRef.current;
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    const contentHeight = element.scrollHeight;
    // layout を持たない環境では 0 になるので CSS の min-height に任せる
    element.style.height = contentHeight > 0 ? `${contentHeight}px` : '';
  }, [value]);

  const handlePickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    // 同じファイルを選び直せるように毎回リセットする
    e.target.value = '';
  };

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    const attachments = toChatAttachments(drafts);
    onSend(value.trim(), attachments.length > 0 ? attachments : undefined);
    setValue('');
    clearDrafts();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Wrapper>
      <ComposerArea>
        <ComposerShell isDisabled={disabled}>
          {drafts.length > 0 && (
            <AttachmentStrip>
              {drafts.map((draft) => (
                <DraftAttachment key={draft.id} draft={draft} onRemove={removeDraft} />
              ))}
            </AttachmentStrip>
          )}
          {attachmentError && <AttachmentError>{attachmentError}</AttachmentError>}
          <StyledTextArea
            ref={textAreaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="質問を入力してください..."
            disabled={disabled}
            rows={1}
          />
          <ComposerFooter>
            <ComposerFooterLeft>
              <Tooltip title={`ファイルを添付（${MAX_ATTACHMENTS}件まで）`} placement="top">
                <span>
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || drafts.length >= MAX_ATTACHMENTS}
                    size="small"
                    aria-label="ファイルを添付"
                  >
                    <AddIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT_ATTRIBUTE}
                multiple
                hidden
                aria-label="添付ファイルを選択"
                onChange={handlePickFiles}
              />
            </ComposerFooterLeft>
            <ComposerFooterRight>
              {value.length > MAX_LENGTH - 200 && (
                <CharCount isOver={isOver}>
                  {value.length} / {MAX_LENGTH}
                </CharCount>
              )}
              <SendButton onClick={handleSend} disabled={!canSend} size="small" aria-label="送信">
                <SendIcon sx={{ fontSize: 16 }} />
              </SendButton>
            </ComposerFooterRight>
          </ComposerFooter>
        </ComposerShell>
      </ComposerArea>
      <Disclaimer>
        AIアシスタントの回答は必ずしも正しいとは限りません。重要な情報は確認するようにしてください。
      </Disclaimer>
    </Wrapper>
  );
}
